import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getStaffToken } from '../api/staff';
import type { StaffMessage, StaffTicket } from '../api/staff';

type SocketHandlers = {
  onNewMessage?: (message: StaffMessage & { ticket_id: number }) => void;
  onNewTicket?: (ticket: Partial<StaffTicket>) => void;
  onTicketUpdated?: (ticket: Partial<StaffTicket>) => void;
  onTicketAssigned?: (data: { ticketId: number }) => void;
};

let sharedSocket: Socket | null = null;
let sharedToken: string | null = null;
const joinedRooms = new Set<number>();

function ensureSocket(): Socket | null {
  const token = getStaffToken();
  if (!token) return null;

  if (sharedSocket && sharedToken !== token) {
    sharedSocket.disconnect();
    sharedSocket = null;
    sharedToken = null;
  }

  if (!sharedSocket) {
    sharedToken = token;
    sharedSocket = io({
      path: '/socket.io/',
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    const rejoinRooms = () => {
      for (const ticketId of joinedRooms) {
        sharedSocket?.emit('ticket:join', { ticketId });
      }
    };

    sharedSocket.on('connect', rejoinRooms);
  }

  return sharedSocket;
}

export function useStaffSocket(handlers: SocketHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const socket = ensureSocket();
    if (!socket) return;

    const onMessage = (data: { ticketId: number; message: StaffMessage }) => {
      handlersRef.current.onNewMessage?.({
        ...data.message,
        ticket_id: data.ticketId,
      });
    };

    const onNewTicket = (ticket: Partial<StaffTicket>) => {
      handlersRef.current.onNewTicket?.(ticket);
    };

    const onTicketUpdated = (ticket: Partial<StaffTicket>) => {
      handlersRef.current.onTicketUpdated?.(ticket);
    };

    const onTicketAssigned = (data: { ticketId: number }) => {
      handlersRef.current.onTicketAssigned?.(data);
    };

    socket.on('message:new', onMessage);
    socket.on('ticket:new', onNewTicket);
    socket.on('ticket:updated', onTicketUpdated);
    socket.on('ticket:assigned', onTicketAssigned);

    return () => {
      socket.off('message:new', onMessage);
      socket.off('ticket:new', onNewTicket);
      socket.off('ticket:updated', onTicketUpdated);
      socket.off('ticket:assigned', onTicketAssigned);
    };
  }, []);
}

export function joinTicketRoom(ticketId: number) {
  joinedRooms.add(ticketId);
  const socket = ensureSocket();
  if (!socket) return;

  const doJoin = () => socket.emit('ticket:join', { ticketId });
  if (socket.connected) {
    doJoin();
  } else {
    socket.once('connect', doJoin);
  }
}

export function leaveTicketRoom(ticketId: number) {
  joinedRooms.delete(ticketId);
  ensureSocket()?.emit('ticket:leave', { ticketId });
}

export function disconnectStaffSocket() {
  if (sharedSocket) {
    sharedSocket.disconnect();
    sharedSocket = null;
    sharedToken = null;
  }
  joinedRooms.clear();
}
